import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { DisposicionSitio } from "@/components/medam/sitio";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ESCOLARIDADES,
  LEYENDA_CONVOCATORIA,
  PREGUNTAS_POSTULACION,
  TURNOS,
} from "@/lib/catalogos";
import { listarPosicionesPublicas, obtenerPosicionPublica } from "@/lib/posiciones.functions";
import { crearPostulacion, type EntradaPostulacion } from "@/lib/postulaciones.functions";
import { subirArchivoPostulacion } from "@/lib/uploads.functions";

const posicionQuery = (id: string) =>
  queryOptions({
    queryKey: ["posicion", id],
    queryFn: () => obtenerPosicionPublica({ id }),
  });

export const Route = createFileRoute("/posiciones/$id/postular")({
  loader: async ({ context, params }) => {
    const posicion = await context.queryClient.ensureQueryData(posicionQuery(params.id));
    if (!posicion) throw notFound();
    return { nombre: posicion.nombre };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData
          ? `Postulación: ${loaderData.nombre} | MEDAM`
          : "Postulación no disponible | MEDAM",
      },
      {
        name: "description",
        content:
          "Completa tu postulación a MEDAM en seis pasos: posición, datos personales, experiencia, preguntas, documentos y confirmación.",
      },
      { property: "og:title", content: "Postulación | MEDAM" },
      {
        property: "og:description",
        content: "Envía tu postulación para formar parte del equipo de MEDAM.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error }) => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">No pudimos abrir el formulario</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      </div>
    </DisposicionSitio>
  ),
  notFoundComponent: () => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">Esta posición ya no está disponible</h1>
        <Button asChild className="mt-6">
          <Link to="/posiciones">Ver posiciones</Link>
        </Button>
      </div>
    </DisposicionSitio>
  ),
  component: Postular,
});

type Borrador = {
  turno_preferencia: string;
  disponibilidad_otro_turno: string;
  fecha_incorporacion: string;
  segunda_opcion_id: string;
  nombre_completo: string;
  fecha_nacimiento: string;
  telefono: string;
  correo: string;
  municipio: string;
  area_experiencia: string;
  ultimo_puesto: string;
  anios_experiencia: string;
  escolaridad: string;
  carrera: string;
  institucion: string;
  cedula_profesional: string;
  certificaciones: string;
  disponibilidad_horario: string;
  disponibilidad_capacitacion: string;
  respuestas: string[];
};

const BORRADOR_INICIAL: Borrador = {
  turno_preferencia: "",
  disponibilidad_otro_turno: "",
  fecha_incorporacion: "",
  segunda_opcion_id: "",
  nombre_completo: "",
  fecha_nacimiento: "",
  telefono: "",
  correo: "",
  municipio: "",
  area_experiencia: "",
  ultimo_puesto: "",
  anios_experiencia: "",
  escolaridad: "",
  carrera: "",
  institucion: "",
  cedula_profesional: "",
  certificaciones: "",
  disponibilidad_horario: "",
  disponibilidad_capacitacion: "",
  respuestas: PREGUNTAS_POSTULACION.map(() => ""),
};

const PASOS = ["Posición", "Datos personales", "Experiencia", "Preguntas", "Confirmación"];


const SIN_SEGUNDA = "ninguna";
const MINIMO_RESPUESTA = 120;

function Postular() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: posicion, isLoading, isError, error } = useQuery(posicionQuery(id));
  const { data: otras = [] } = useQuery({
    queryKey: ["posiciones", "publicas"],
    queryFn: () => listarPosicionesPublicas(),
  });

  if (isLoading) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">Cargando formulario...</h1>
        </div>
      </DisposicionSitio>
    );
  }

  if (isError) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">No pudimos abrir el formulario</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "Intenta nuevamente en unos momentos."}
          </p>
        </div>
      </DisposicionSitio>
    );
  }

  const claveBorrador = `medam-postulacion-${id}`;
  const [paso, setPaso] = useState(0);
  const [datos, setDatos] = useState<Borrador>(BORRADOR_INICIAL);
  const [acepta, setAcepta] = useState(false);
  const [errores, setErrores] = useState<string[]>([]);
  const [hidratado, setHidratado] = useState(false);
  const [archivos, setArchivos] = useState<Record<string, File | null>>({});

  useEffect(() => {
    const guardado = window.localStorage.getItem(claveBorrador);
    if (guardado) {
      try {
        setDatos({ ...BORRADOR_INICIAL, ...(JSON.parse(guardado) as Partial<Borrador>) });
      } catch {
        /* borrador inválido */
      }
    }
    setHidratado(true);
  }, [claveBorrador]);

  useEffect(() => {
    if (hidratado) window.localStorage.setItem(claveBorrador, JSON.stringify(datos));
  }, [datos, claveBorrador, hidratado]);

  const actualizar = (campo: keyof Borrador, valor: string) =>
    setDatos((previo) => ({ ...previo, [campo]: valor }));

  const enviar = useMutation({
    mutationFn: async () => {
      const documentos: EntradaPostulacion["documentos"] = [];
      const cargas = Object.entries(archivos).filter(([, file]) => file instanceof File) as Array<
        [string, File]
      >;

      for (const [tipo, file] of cargas) {
        const documento = await subirArchivoPostulacion({ file, tipo });
        documentos.push(documento);
      }



      return crearPostulacion({
        posicion_id: id,
        segunda_opcion_id:
          datos.segunda_opcion_id && datos.segunda_opcion_id !== SIN_SEGUNDA
            ? datos.segunda_opcion_id
            : null,
        turno_preferencia: datos.turno_preferencia,
        disponibilidad_otro_turno: datos.disponibilidad_otro_turno,
        fecha_incorporacion: datos.fecha_incorporacion || null,
        postulante: {
          nombre_completo: datos.nombre_completo,
          fecha_nacimiento: datos.fecha_nacimiento || null,
          telefono: datos.telefono,
          correo: datos.correo,
          municipio: datos.municipio,
          area_experiencia: datos.area_experiencia,
          ultimo_puesto: datos.ultimo_puesto,
          anios_experiencia: Number(datos.anios_experiencia || 0),
          escolaridad: datos.escolaridad,
          carrera: datos.carrera,
          institucion: datos.institucion,
          cedula_profesional: datos.cedula_profesional || null,
          certificaciones: datos.certificaciones || null,
          disponibilidad_horario: datos.disponibilidad_horario,
          disponibilidad_capacitacion: datos.disponibilidad_capacitacion,
        },
        respuestas: PREGUNTAS_POSTULACION.map((pregunta, indice) => ({
          pregunta,
          respuesta: datos.respuestas[indice] ?? "",
        })),
        documentos,
      });
    },
    onSuccess: (resultado) => {
      window.localStorage.removeItem(claveBorrador);
      navigate({
        to: "/postulacion/confirmacion",
        search: { folio: resultado.folio, posicion: posicion?.nombre ?? "" },
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const validar = (indice: number): string[] => {
    const faltantes: string[] = [];
    if (indice === 0) {
      if (!datos.turno_preferencia) faltantes.push("Selecciona tu turno de preferencia.");
      if (!datos.disponibilidad_otro_turno)
        faltantes.push("Indica si tienes disponibilidad para otro turno.");
    }
    if (indice === 1) {
      if (datos.nombre_completo.trim().length < 5) faltantes.push("Escribe tu nombre completo.");
      if (datos.telefono.replace(/\D/g, "").length < 8)
        faltantes.push("Escribe un teléfono de contacto válido.");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(datos.correo.trim()))
        faltantes.push("Escribe un correo electrónico válido.");
      if (datos.municipio.trim().length < 2) faltantes.push("Indica tu municipio de residencia.");
    }
    if (indice === 2) {
      if (!datos.ultimo_puesto.trim()) faltantes.push("Indica tu último puesto.");
      if (datos.anios_experiencia === "") faltantes.push("Indica tus años de experiencia.");
      if (!datos.escolaridad) faltantes.push("Selecciona tu escolaridad.");
    }
    if (indice === 3) {
      PREGUNTAS_POSTULACION.forEach((_, i) => {
        if ((datos.respuestas[i] ?? "").trim().length < MINIMO_RESPUESTA)
          faltantes.push(`La respuesta ${i + 1} debe tener al menos ${MINIMO_RESPUESTA} caracteres.`);
      });
      if (Object.values(archivos).filter((archivo) => archivo instanceof File).length === 0) {
        faltantes.push("Adjunta al menos un documento para continuar.");
      }
    }
    if (indice === 4 && !acepta) {
      faltantes.push("Debes aceptar el compromiso y el uso de tus datos para continuar.");
    }

    return faltantes;
  };

  const avanzar = () => {
    const faltantes = validar(paso);
    setErrores(faltantes);
    if (faltantes.length) return;
    if (paso === PASOS.length - 1) {
      enviar.mutate();
      return;
    }
    setPaso((actual) => actual + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const segundaOpciones = useMemo(() => otras.filter((p) => p.id !== id), [otras, id]);

  if (!posicion) return null;

  return (
    <DisposicionSitio>
      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <p className="text-sm text-muted-foreground">Postulación</p>
        <h1 className="font-display text-3xl">{posicion.nombre}</h1>

        <ol className="mt-8 flex flex-wrap gap-2" aria-label="Progreso de la postulación">
          {PASOS.map((nombre, indice) => (
            <li
              key={nombre}
              aria-current={indice === paso ? "step" : undefined}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                indice === paso
                  ? "border-primary bg-primary text-primary-foreground"
                  : indice < paso
                    ? "border-primary/30 bg-secondary text-foreground"
                    : "border-border text-muted-foreground"
              }`}
            >
              {indice < paso ? <Check className="size-3" /> : <span>{indice + 1}</span>}
              {nombre}
            </li>
          ))}
        </ol>

        <div className="mt-8 rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
          {paso === 0 ? (
            <Seccion titulo="Posición de interés">
              <Campo etiqueta="Posición seleccionada">
                <Input value={posicion.nombre} readOnly className="bg-muted" />
              </Campo>
              <Campo etiqueta="Segunda opción (opcional)">
                <Select
                  value={datos.segunda_opcion_id || SIN_SEGUNDA}
                  onValueChange={(valor) => actualizar("segunda_opcion_id", valor)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin segunda opción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SIN_SEGUNDA}>Sin segunda opción</SelectItem>
                    {segundaOpciones.map((opcion) => (
                      <SelectItem key={opcion.id} value={opcion.id}>
                        {opcion.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
              <Campo etiqueta="Turno de preferencia">
                <Select
                  value={datos.turno_preferencia}
                  onValueChange={(valor) => actualizar("turno_preferencia", valor)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un turno" />
                  </SelectTrigger>
                  <SelectContent>
                    {TURNOS.map((turno) => (
                      <SelectItem key={turno} value={turno}>
                        {turno}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
              <Campo etiqueta="¿Tienes disponibilidad para otro turno?">
                <Select
                  value={datos.disponibilidad_otro_turno}
                  onValueChange={(valor) => actualizar("disponibilidad_otro_turno", valor)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sí">Sí</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Por definir">Por definir</SelectItem>
                  </SelectContent>
                </Select>
              </Campo>
            </Seccion>
          ) : null}

          {paso === 1 ? (
            <Seccion titulo="Datos personales">
              <Campo etiqueta="Nombre completo">
                <Input
                  value={datos.nombre_completo}
                  maxLength={120}
                  onChange={(evento) => actualizar("nombre_completo", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Fecha de nacimiento (opcional)">
                <Input
                  type="date"
                  value={datos.fecha_nacimiento}
                  onChange={(evento) => actualizar("fecha_nacimiento", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Teléfono de contacto">
                <Input
                  inputMode="tel"
                  maxLength={25}
                  value={datos.telefono}
                  onChange={(evento) => actualizar("telefono", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Correo electrónico">
                <Input
                  type="email"
                  maxLength={160}
                  value={datos.correo}
                  onChange={(evento) => actualizar("correo", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Municipio o ciudad de residencia">
                <Input
                  maxLength={120}
                  value={datos.municipio}
                  onChange={(evento) => actualizar("municipio", evento.target.value)}
                />
              </Campo>
            </Seccion>
          ) : null}

          {paso === 2 ? (
            <Seccion titulo="Experiencia">
              <p className="text-sm text-muted-foreground">
                Solo necesitamos un panorama general de tu trayectoria.
              </p>
              <Campo etiqueta="Último puesto desempeñado">
                <Input
                  maxLength={160}
                  value={datos.ultimo_puesto}
                  onChange={(evento) => actualizar("ultimo_puesto", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Años de experiencia">
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={datos.anios_experiencia}
                  onChange={(evento) => actualizar("anios_experiencia", evento.target.value)}
                />
              </Campo>
              <Campo etiqueta="Escolaridad">
                <Select
                  value={datos.escolaridad}
                  onValueChange={(valor) => actualizar("escolaridad", valor)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tu escolaridad" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESCOLARIDADES.map((nivel) => (
                      <SelectItem key={nivel} value={nivel}>
                        {nivel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            </Seccion>
          ) : null}


          {paso === 3 ? (
            <Seccion titulo="Preguntas de valoración">
              <p className="text-sm text-muted-foreground">
                Responde con detalle: cada respuesta debe tener al menos {MINIMO_RESPUESTA}{" "}
                caracteres.
              </p>
              {PREGUNTAS_POSTULACION.map((pregunta, indice) => {
                const valor = datos.respuestas[indice] ?? "";
                return (
                  <Campo key={pregunta} etiqueta={`${indice + 1}. ${pregunta}`}>
                    <Textarea
                      rows={5}
                      maxLength={2000}
                      value={valor}
                      onChange={(evento) =>
                        setDatos((previo) => {
                          const respuestas = [...previo.respuestas];
                          respuestas[indice] = evento.target.value;
                          return { ...previo, respuestas };
                        })
                      }
                    />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {valor.trim().length}/{MINIMO_RESPUESTA} caracteres mínimos
                    </span>
                  </Campo>
                );
              })}
              <div className="space-y-3 rounded-xl border border-border/70 p-4">
                <p className="text-sm font-medium">Documentos para la postulación</p>
                <Campo etiqueta="CV o currículo">
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(evento) =>
                      setArchivos((previo) => ({
                        ...previo,
                        cv: evento.target.files?.[0] ?? null,
                      }))
                    }
                  />
                </Campo>
                <Campo etiqueta="Otros documentos relevantes (opcional)">
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(evento) =>
                      setArchivos((previo) => ({
                        ...previo,
                        otros: evento.target.files?.[0] ?? null,
                      }))
                    }
                  />
                </Campo>
              </div>
            </Seccion>
          ) : null}

          {paso === 4 ? (
            <Seccion titulo="Revisión y confirmación">
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <Resumen etiqueta="Posición" valor={posicion.nombre} />
                <Resumen etiqueta="Turno de preferencia" valor={datos.turno_preferencia} />
                <Resumen etiqueta="Nombre" valor={datos.nombre_completo} />
                <Resumen etiqueta="Correo" valor={datos.correo} />
                <Resumen etiqueta="Teléfono" valor={datos.telefono} />
                <Resumen etiqueta="Municipio" valor={datos.municipio} />
                <Resumen etiqueta="Último puesto" valor={datos.ultimo_puesto} />
                <Resumen etiqueta="Años de experiencia" valor={datos.anios_experiencia} />
              </dl>
              <p className="rounded-2xl border border-primary/25 bg-secondary/50 p-4 text-sm leading-relaxed">
                Al confirmar tu solicitud aceptas que estás dispuesto a cambiar procesos, mejorar la
                atención y ser un mejor profesional en pro de la salud de las personas.
              </p>
              <p className="rounded-xl border border-border bg-secondary/50 p-4 text-sm leading-relaxed text-muted-foreground">
                {LEYENDA_CONVOCATORIA}
              </p>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  checked={acepta}
                  onCheckedChange={(valor) => setAcepta(valor === true)}
                  className="mt-0.5"
                />
                <span>
                  Confirmo que la información es verídica, acepto el compromiso anterior y autorizo a
                  MEDAM a utilizar mis datos únicamente para este proceso de selección.
                </span>
              </label>
            </Seccion>
          ) : null}


          {errores.length ? (
            <ul
              role="alert"
              className="mt-6 space-y-1 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
            >
              {errores.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          ) : null}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="outline"
              disabled={paso === 0 || enviar.isPending}
              onClick={() => {
                setErrores([]);
                setPaso((actual) => Math.max(0, actual - 1));
              }}
            >
              Regresar
            </Button>
            <Button type="button" onClick={avanzar} disabled={enviar.isPending}>
              {enviar.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {paso === PASOS.length - 1 ? "Enviar postulación" : "Continuar"}
            </Button>
          </div>
        </div>
      </div>
    </DisposicionSitio>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-5">
      <h2 className="font-display text-xl">{titulo}</h2>
      {children}
    </section>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium leading-snug">{etiqueta}</Label>
      {children}
    </div>
  );
}

function Resumen({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="rounded-lg border border-border/70 p-3">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{etiqueta}</dt>
      <dd className="mt-0.5 font-medium">{valor || "—"}</dd>
    </div>
  );
}
